<script lang="ts">
  import "daterangepicker";
  import "/../css/datepicker.css";
  import Avatar from "/traveller/components/Avatar.svelte";
  import Select from "/credit/components/CouponRecommenderSelect.svelte";
  import { slide } from "svelte/transition";

  import { onMount, createEventDispatcher } from "svelte";
  import api from "../api";
  import { notification } from "/shared/utils.js";
  import { currentTraveller } from "/traveller/stores";
  import { BookingEnquirySerialiserRequest, BusinessSerialiser } from "/apiFactory";
  import { getFullHighlight } from "/traveller/highlights/api";
  import creditApi from "/credit/api";
  import DiscountPanel from "./DiscountPanel.svelte";
  import HCaptcha from "/shared/HCaptcha.svelte";

  export let business: BusinessSerialiser;

  let selectedRecommender;
  const dispatch = createEventDispatcher();
  let captcha;

  let form: BookingEnquirySerialiserRequest = {
    business_profile: business.business_code,
    coupon: undefined,
    guest_name: $currentTraveller.name,
    guest_email: $currentTraveller.email,
    recommender: undefined,
    token: undefined,
  };

  const getCoupon = async () => {
    let response = await creditApi.creditApiCouponList(
      "false",
      business.business_code,
      1,
      $currentTraveller.code
    );
    let coupon = response.data.results?.[0];
    if (coupon) {
      // update form
      form.coupon = coupon.code;
      return coupon;
    } else {
      throw new Error("No coupon found for traveller");
    }
  };

  const getRecommenders = async () => {
    let highlight = await getFullHighlight(business.business_code);
    let choices = [];
    // reshape data for Select component
    highlight.visits.forEach((visit) => {
      // only show other travellers as suggestions
      if ($currentTraveller.code != visit.traveller_code) {
        choices.push({
          code: visit.traveller.code,
          selfie: visit.traveller.selfie,
          name: visit.traveller.name,
        });
      }
    });
    return choices;
  };

  let couponPromise;
  let recommendersPromise;

  // check for coupons for travellers
  $: if ($currentTraveller.code) {
    couponPromise = getCoupon();
    // only check for recommenders if user has no coupons
    recommendersPromise = couponPromise.then(
      () => [], // got a coupon
      () => getRecommenders()
    );
  }

  onMount(() => {
    // attach a daterangepicker to the 'dates' form field,
    // component must be mounted on the DOM first.
    let dates = jQuery("input[name='daterange']");
    dates.daterangepicker({
      autoUpdateInput: true,
      locale: {
        cancelLabel: "Clear", // dates are optional
      },
    });

    // clear default values
    dates.val("");
    dates.on("apply.daterangepicker", function (event, picker) {
      form.from_date = picker.startDate.format("YYYY-MM-DD");
      form.to_date = picker.endDate.format("YYYY-MM-DD");
    });

    // clear values on 'cancel'
    dates.on("cancel.daterangepicker", function () {
      dates.val("");
      form.from_date = null;
      form.to_date = null;
    });
  });

  const submitEnquiry = async (event) => {
    event.preventDefault();

    // don't submit if captcha not good
    if (!form.token) return;

    if (selectedRecommender) {
      form.recommender = selectedRecommender.value.code;
    }
    try {
      await api.apiBookingEnquiryCreate(form);
      notification.success("Sent enquiry");
      dispatch("success");
    } catch (error) {
      // handle potential captcha errors
      if (error.response?.data?.token) {
        captcha.reset();
        notification.error("Captcha verification failed. Please try again.");
        return;
      }
      notification.reportError(error);
      dispatch("failure");
    }
  };

  /* hCaptcha controls */
  const handleHCaptchaSuccess = (ev) => {
    form.token = ev.detail.token;
  };
</script>

<style lang="scss">
  // Import theme colours
  @use "sass/user/colours";
  .lower-label {
    color: colours.$gray-600;
  }
  .recommender {
    background-color: colours.$gray-200;
  }
  .large-control {
    height: 45px;
  }
  :global(form .avatar-traveller) {
    height: 30px;
    width: 30px;
  }
</style>

<form on:submit={submitEnquiry} class="text-center text-lower">
  <div class="container">
    {#if $currentTraveller.code}
      {#await couponPromise then coupon}
        <!-- traveller already has a coupon for this business -->
        <div transition:slide>
          <DiscountPanel discount={coupon.discount} />
        </div>
      {:catch}
        <!-- traveller doesn't have a coupon, yet -->
        {#await recommendersPromise then recommenders}
          {#if recommenders.length && business.discount}
            <div transition:slide>
              <DiscountPanel discount={business.discount} />
            </div>
          {/if}
        {/await}
      {/await}
    {/if}

    <div class="row my-3">
      <div class="col-12">
        <div id="dates" class="form-group">
          <label for="dates" class="lower-label font-weight-bold requiredField">
            Check In / Check Out
          </label>
          <div>
            <input
              type="text"
              class="form-control text-center large-control"
              name="daterange" />
          </div>
        </div>

        <div class="form-group">
          <label for="guest_name" class="lower-label font-weight-bold">
            Your Name*
          </label>
          <input
            name="guest_name"
            class="form-control text-center large-control"
            bind:value={form.guest_name}
            required />
        </div>

        <div class="form-group">
          <label for="guest_email" class="lower-label font-weight-bold">
            Email Address*
          </label>
          <input
            name="guest_email"
            type="email"
            class="form-control text-center large-control"
            bind:value={form.guest_email}
            required />
        </div>

        <div class="form-group">
          <label for="guest_message" class="lower-label font-weight-bold">
            Information About Your Booking
          </label>
          <!-- prettier-ignore -->
          <textarea
            class="form-control"
            id="recommendation-text"
            aria-describedby="bookingMessage"
            rows="4"
            name="guest_message"
            placeholder="Add information about your booking – including number of guests and any special requests or questions you may have"
            bind:value={form.guest_message} />
        </div>
      </div>
    </div>

    <!-- COUPONS (traveller only) -->
    {#if $currentTraveller.code}
      {#await couponPromise then coupon}
        <!-- traveller already has a coupon for this business -->
        <div transition:slide>
          <div class="row mt-n3">
            <div class="col-12">
              <div class="form-group">
                <label for="recommender" class="mb-0 lower-label font-weight-bold">
                  Recommended By
                </label>
                <div
                  name="recommender"
                  class="form-control text-center large-control recommender">
                  <div class="h-100">
                    <span class="mr-2">
                      <Avatar traveller={coupon.recommender} />
                    </span>
                    {coupon.recommender.name}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      {:catch}
        <!-- traveller doesn't have a coupon, yet -->
        {#await recommendersPromise then recommenders}
          {#if recommenders.length && business.discount}
            <div transition:slide>
              <div class="mt-n3 lower-label font-weight-bold text-center">
                I Want The Bonus Credit To Go To:
              </div>
              <Select bind:selected={selectedRecommender} {recommenders} />
            </div>
          {/if}
        {/await}
      {/await}
    {/if}
    <!-- /COUPONS -->

    <HCaptcha bind:this={captcha} on:success={handleHCaptchaSuccess} />

    <div class="row justify-content-center mt-3">
      <button
        type="button"
        class="btn btn-outline-muted col-4 mx-2"
        on:click={() => dispatch("cancel")}>
        Cancel
      </button>
      <button type="submit" class="btn btn-primary col-4 mx-2">Send</button>
    </div>
  </div>
</form>
